import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { withBoardAuth } from '@generated/server/boardAuth.server';
import { db } from '@vibe/db/server';
import { portalEvents, classifieds } from '@generated/models';
import { PortalOrdersBoard, SubscriptionsBoard } from '@api/BoardSDK';
import { requireStaff } from '@generated/server/staff.server';

export const EVENT_TYPES = [
  'visit', 'view_ads', 'view_subscribe', 'view_merch', 'view_classifieds',
  'ad_started', 'ad_completed', 'sub_completed', 'classified_completed', 'etsy_click',
  'job_sales', 'job_freelance', 'job_phone', 'view_jobs', 'view_editorial', 'view_letters', 'view_mail', 'mail_call', 'mail_email',
] as const;

export const trackEvent = createServerFn({ method: 'POST' })
  .validator(z.object({ type: z.enum(EVENT_TYPES) }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    const now = new Date();
    await db.collection(portalEvents).add({ type: data.type, day: now.toLocaleDateString('en-CA', { timeZone: 'America/New_York' }), createdAt: now.toISOString() });
    return { ok: true };
  }));

const periodStart = (p: string) => {
  const n = new Date();
  if (p === 'month') return new Date(n.getFullYear(), n.getMonth(), 1);
  if (p === '30d') return new Date(n.getTime() - 30 * 86400000);
  if (p === 'year') return new Date(n.getFullYear(), 0, 1);
  return new Date(2000, 0, 1);
};
const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

export const getReport = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), period: z.enum(['30d', 'month', 'year', 'all']) }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    const from = periodStart(data.period);
    const fromDay = from.toISOString().slice(0, 10);
    const toDay = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

    // Views: one count query per event type.
    const viewsP = Promise.all(EVENT_TYPES.map(async (t) => [t, await db.collection(portalEvents).where('type', '==', t).where('day', '>=', fromDay).count()] as const));

    // Ad orders: aggregate API over the date column.
    const intake = new PortalOrdersBoard();
    const adsP = intake.aggregate().where({ orderDate: { between: [fromDay, toDay] } }).sum('totalContractValue', 'total').countItems('count').execute();
    const adStatusP = intake.aggregate().where({ orderDate: { between: [fromDay, toDay] } }).groupBy('status').sum('totalContractValue', 'total').countItems('count').execute();

    // Subscriptions: no date column, so walk every page and bucket by created date.
    const subsP = (async () => {
      const out: { amount: number; created: Date }[] = [];
      let cursor: string | null | undefined;
      do {
        const res = await new SubscriptionsBoard().items().withColumns(['amount']).withPagination({ limit: 100, ...(cursor ? { cursor } : {}) }).execute();
        res.items.forEach((i) => out.push({ amount: i.amount ?? 0, created: new Date(i.createdAt) }));
        cursor = res.cursor;
      } while (cursor);
      return out;
    })();

    const clsP = (async () => {
      const out: { amount: number; created: Date }[] = [];
      let cursor: string | undefined;
      do {
        const q = db.collection(classifieds).limit(100);
        const page = await (cursor ? q.startAfter(cursor) : q).getPage();
        page.docs.forEach((d) => out.push({ amount: d.data.amount ?? 0, created: new Date(d.data.createdAt) }));
        cursor = page.cursor;
      } while (cursor);
      return out;
    })();

    const [views, ads, adStatus, subsAll, clsAll] = await Promise.all([viewsP, adsP, adStatusP, subsP, clsP]);
    const inPeriod = (r: { created: Date }) => r.created >= from;
    const subs = subsAll.filter(inPeriod);
    const cls = clsAll.filter(inPeriod);

    // Last 6 months trend.
    const months: { key: string; label: string; ads: number; subs: number; classifieds: number }[] = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({ key: monthKey(d), label: d.toLocaleString('en-US', { month: 'short' }), ads: 0, subs: 0, classifieds: 0 });
    }
    await Promise.all(months.map(async (m) => {
      const [y, mo] = m.key.split('-').map(Number);
      const start = new Date(y ?? 2000, (mo ?? 1) - 1, 1);
      const end = new Date(y ?? 2000, mo ?? 1, 0);
      const r = await intake.aggregate().where({ orderDate: { between: [start.toISOString().slice(0, 10), end.toISOString().slice(0, 10)] } }).sum('totalContractValue', 'total').execute();
      m.ads = r?.[0]?.total ?? 0;
    }));
    subsAll.forEach((s) => { const m = months.find((x) => x.key === monthKey(s.created)); if (m) m.subs += s.amount; });
    clsAll.forEach((c) => { const m = months.find((x) => x.key === monthKey(c.created)); if (m) m.classifieds += c.amount; });

    return {
      views: Object.fromEntries(views) as Record<(typeof EVENT_TYPES)[number], number>,
      sales: {
        ads: { total: ads?.[0]?.total ?? 0, count: ads?.[0]?.count ?? 0 },
        adStatus: (adStatus ?? []).map((r) => ({ status: r.status ?? 'No status', total: r.total ?? 0, count: r.count ?? 0 })),
        subs: { total: subs.reduce((a, s) => a + s.amount, 0), count: subs.length },
        classifieds: { total: cls.reduce((a, s) => a + s.amount, 0), count: cls.length },
      },
      months,
    };
  }));
